import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import surveyActivationService from '../../services/surveyActivationService';
import { AuthContext } from '../../contexts/AuthContext';
import { canActivateVisits } from '../../utils/activationPermissions';

/**
 * WindowActivationButton - A reusable button component that works with the activateOrDeactivateWindow function
 * to activate or deactivate survey windows for a participant.
 * 
 * @component
 */
const WindowActivationButton = ({
  participantId,
  windowIds,
  activate = true,
  customLabel,
  customStyles = {},
  variant = 'primary',
  size = 'sm',
  className = '',
  onSuccess = () => {},
  onError = () => {},
  disabled = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(AuthContext);
  
  // Check if user has permission to activate/deactivate visits
  const hasPermission = canActivateVisits(user);
  
  // If user doesn't have permission, don't render the button
  if (!hasPermission) {
    return null;
  }
  
  // Determine the button text based on the activate flag and customLabel
  const getButtonText = () => {
    if (customLabel) return customLabel;
    return activate ? 'Activate Window' : 'Deactivate Window';
  };

  // Handle the button click event
  const handleClick = async () => {
    if (!participantId || !windowIds || windowIds.length === 0) {
      toast.error('Missing required parameters: participantId or windowIds');
      onError(new Error('Missing required parameters'));
      return;
    }

    setIsLoading(true);
    try {
      const result = await surveyActivationService.activateOrDeactivateWindow(
        participantId,
        windowIds,
        activate
      );
      
      // Show success message
      const actionText = activate ? 'activated' : 'deactivated';
      toast.success(`Window successfully ${actionText}`);
      
      // Call onSuccess callback with the result
      onSuccess(result);
    } catch (error) {
      // Show error message
      const actionText = activate ? 'activating' : 'deactivating';
      toast.error(`Error ${actionText} window: ${error.message || 'Unknown error'}`);
      
      // Call onError callback with the error
      onError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Bootstrap-style variant and size names map onto the site's buttons.
  const variantClass = /danger/.test(variant)
    ? 'btn-danger'
    : /outline|secondary|light|link/.test(variant)
      ? 'btn-secondary'
      : 'btn-primary';
  const sizeClass = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';

  return (
    <button
      type="button"
      className={`btn ${variantClass} ${sizeClass} ${className}`}
      onClick={handleClick}
      disabled={disabled || isLoading}
      style={customStyles}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>Processing...</span>
        </>
      ) : (
        getButtonText()
      )}
    </button>
  );
};

WindowActivationButton.propTypes = {
  /** Participant ID for the window activation/deactivation */
  participantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  
  /** Array of window IDs to activate/deactivate */
  windowIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  
  /** Flag to determine if activating (true) or deactivating (false) the window */
  activate: PropTypes.bool,
  
  /** Custom label text for the button */
  customLabel: PropTypes.string,
  
  /** Custom styles object for the button */
  customStyles: PropTypes.object,
  
  /** Button variant: primary, secondary or danger (Bootstrap names also accepted) */
  variant: PropTypes.string,
  
  /** Button size: sm, md or lg */
  size: PropTypes.string,
  
  /** Additional CSS class names */
  className: PropTypes.string,
  
  /** Callback function called on successful activation/deactivation */
  onSuccess: PropTypes.func,
  
  /** Callback function called on error */
  onError: PropTypes.func,
  
  /** Flag to disable the button */
  disabled: PropTypes.bool
};

export default WindowActivationButton;